<template>
  <v-form v-model="isFormValid" ref="form">
    <v-text-field
      required
      outlined
      dense
      data-private
      class="body2"
      data-testid="createAccount-input-email"
      v-model="account.email"
      append-icon="mdi-lock"
      :error-messages="errors.email"
      :label="`${$t('auth.register.email')}*`"
      :disabled="emailFieldIsDisabled"
      :rules="[name => !!name || $t('validation.form.this_field_is_required')]"
      @input="$emit('onChange', $event, 'email')"
      @blur="$emit('onBlur', $event, 'email')"
    ></v-text-field>

    <v-text-field
      required
      outlined
      dense
      data-private
      class="body2"
      data-testid="createAccount-input-firstName"
      :error-messages="errors.displayname"
      v-model="account.displayname"
      :label="`${$t('auth.register.first_name')}*`"
      :rules="[name => !!name || $t('validation.form.this_field_is_required')]"
      @input="$emit('onChange', $event, 'displayname')"
      @blur="$emit('onBlur', $event, 'displayname')"
    ></v-text-field>

    <v-text-field
      required
      outlined
      dense
      data-private
      class="body2"
      data-testid="createAccount-input-lastName"
      v-model="account.familyname"
      :error-messages="errors.familyname"
      :label="`${$t('auth.register.last_name')}*`"
      :rules="[name => !!name || $t('validation.form.this_field_is_required')]"
      @input="$emit('onChange', $event, 'familyname')"
      @blur="$emit('onBlur', $event, 'familyname')"
    ></v-text-field>

    <v-text-field
      required
      outlined
      dense
      data-private
      data-testid="createAccount-input-position"
      v-model="account.position"
      :maxlength="$constants.profile.positionMaxLength"
      :label="`${$t('profile_settings.job_position')}*`"
      :rules="[name => !!name || $t('validation.form.this_field_is_required')]"
      @input="$emit('onChange', $event, 'position')"
      @blur="$emit('onBlur', $event, 'position')"
    ></v-text-field>

    <AddPassword
      testId="createAccount-input-password"
      showDifferentPasswordLabel
      :error="errors.password"
      :password="form.password"
      :confirmPassword="form.confirmPassword"
      :customPasswordLabel="`${$t('common.users.new_password_label')}*`"
      :customConfirmPasswordLabel="
        `${$t('common.users.new_password_confirmation_label')}*`
      "
      @change="handlePasswordChange"
    />

    <div
      v-if="showTermsOfService"
      class="d-flex align-center toc-checkbox-container body-2 mt-4"
    >
      <v-checkbox
        hide-details="auto"
        data-testid="createAccount-checkbox-tServices"
        v-model="account.tocsHasAccepted"
      />

      <label class="mb-0"
        >{{ $t('auth.register.i_agree_to_the_tocs') }}
        <a target="_blank" :href="$t('auth.access.terms_of_service_url')">{{
          $t('auth.register.terms_of_service')
        }}</a>
        {{ $t('common.and') }}
        <a target="_blank" :href="$t('auth.access.privacy_policy_url')">{{
          $t('auth.access.privacy_policy')
        }}</a>
      </label>
    </div>

    <div v-if="showGDPRCheckbox" class="d-flex align-center body-2">
      <v-checkbox
        class="mt-0"
        hide-details="auto"
        data-testid="createAccount-checkbox-gdpr"
        v-model="account.gdprApproval"
      />

      <label class="mt-2"
        >{{ $t('auth.register.i_agree_to_receive_marketing_communications') }}
      </label>
    </div>

    <Button
      block
      size="large"
      data-testid="createAccount-button-next"
      class="mt-6 mb-4"
      color="primary"
      :disabled="requiresTOCs || !isFormValid"
      :loading="isLoading"
      :text="buttonText"
      @click="onSubmit"
    />
  </v-form>
</template>

<script>
import Button from '@/components/UI/Button'
import AddPassword from '@/components/AddPassword.vue'

export default {
  props: {
    account: {
      type: Object,
      required: false,
    },
    buttonText: {
      type: String,
      required: true,
    },
    emailFieldIsDisabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    showTermsOfService: {
      type: Boolean,
      required: false,
      default: false,
    },
    showGDPRCheckbox: {
      type: Boolean,
      required: false,
      default: false,
    },
    errors: {
      type: Object,
      required: false,
    },
    isLoading: Boolean,
  },

  data() {
    return {
      isFormValid: false,
      form: {
        password: '',
        confirmPassword: '',
      },
    }
  },

  components: {
    AddPassword,
    Button,
  },

  computed: {
    requiresTOCs() {
      return this.showTermsOfService && !this.account.tocsHasAccepted
    },
  },

  methods: {
    handlePasswordChange(payload) {
      this.form = { ...this.form, ...payload }
      if (payload.password) {
        this.$emit('onPasswordUpdated', this.form.password)
      }
    },

    onSubmit() {
      this.$emit('onSubmit', this.account)
    },
  },

  watch: {
    async errors() {
      await this.$nextTick()
      this.$refs.form.validate()
    },
  },
}
</script>

<style lang="scss" scoped>
.toc-checkbox-container {
  .v-input {
    margin-top: 0;
    padding-top: 0;
  }
}
</style>
